/* eslint-disable react-refresh/only-export-components -- shared helpers live with the table components */
import {
  Button,
  Chip,
  Division,
  EmptyState,
  Flex,
  Heading,
  Icons,
  Paragraph,
  Table,
  type TableColumnType,
  type TableColumnVerticalAlign,
  type TableProps,
  type TableRowSelectionType,
} from "@ir/ui";
import { useMemo } from "react";
import { useAutoHeight } from "@src/presentation/hooks/autoheight";

export const CONFIG_COLUMN_VERTICAL_ALIGN: TableColumnVerticalAlign = "middle";

export function renderConfigStatusChip(value: unknown) {
  return (
    <Flex align="center" justifyContent="start">
      <Chip
        label={value ? "Active" : "Inactive"}
        color={value ? "success" : "error"}
        size="small"
      />
    </Flex>
  );
}

/**
 * `@ir/ui` Table only distributes column widths when `scroll.x` is a number,
 * so the total has to be derived from the column definitions.
 */
export function sumConfigColumnWidths<TRow>(
  columns: TableColumnType<TRow>[],
): number {
  return columns.reduce(
    (total, column) =>
      total + (typeof column.width === "number" ? column.width : 0),
    0,
  );
}

interface ConfigTableEmptyStateProps {
  search: string;
  entityLabel: string;
  onClearSearch: () => void;
}

/** Shared empty / no-results UI for Config list tables. */
export function ConfigTableEmptyState({
  search,
  entityLabel,
  onClearSearch,
}: Readonly<ConfigTableEmptyStateProps>) {
  const trimmedSearch = search.trim();
  const hasActiveSearch = trimmedSearch.length > 0;

  if (hasActiveSearch) {
    return (
      <EmptyState
        className="px-3 py-4"
        icon={Icons.InfoCircle}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-2">
            No results found for &quot;{trimmedSearch}&quot;
          </Heading>
        }
        description={
          <>
            <Paragraph className="text-grey-500 font-regular mb-0">
              Try adjusting your search terms, or clear the search to see all{" "}
              {entityLabel}.
            </Paragraph>
            <Button className="mt-3" variant="clear" onPress={onClearSearch}>
              Clear search
            </Button>
          </>
        }
      />
    );
  }

  return (
    <EmptyState
      className="px-3 py-4"
      icon={Icons.Rows01}
      title={
        <Heading level={6} className="text-grey-700 font-bold mt-2">
          No data for this brand
        </Heading>
      }
      description={
        <Paragraph className="text-grey-500 font-regular mb-0">
          There is no configuration data available for the selected brand.
        </Paragraph>
      }
    />
  );
}

interface ConfigListTableProps<TRow extends { key: string }> {
  columns: TableColumnType<TRow>[];
  dataSource: TRow[];
  search: string;
  entityLabel: string;
  ariaLabel: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  rowSelection?: TableRowSelectionType<TRow>;
  rowClassName?: NonNullable<TableProps<TRow>["rowClassName"]>;
}

/** Shared scrollable table shell for Config list pages. */
export function ConfigListTable<TRow extends { key: string }>({
  columns,
  dataSource,
  search,
  entityLabel,
  ariaLabel,
  setSearch,
  setPage,
  rowSelection,
  rowClassName,
}: Readonly<ConfigListTableProps<TRow>>) {
  const { ref: tableScrollContainerRef, height: tableHeight } =
    useAutoHeight<HTMLDivElement>();
  const tableWidth = useMemo(() => {
    const columnsWidth = sumConfigColumnWidths(columns);
    if (!rowSelection) {
      return columnsWidth;
    }
    const selectionWidth =
      typeof rowSelection.columnWidth === "number"
        ? rowSelection.columnWidth
        : 55;
    return columnsWidth + selectionWidth;
  }, [columns, rowSelection]);

  const clearSearch = () => {
    setPage(1);
    setSearch("");
  };

  return (
    <Division
      ref={tableScrollContainerRef}
      style={{ flex: 1, minHeight: 0, minWidth: 0 }}
    >
      {dataSource.length === 0 ? (
        <ConfigTableEmptyState
          search={search}
          entityLabel={entityLabel}
          onClearSearch={clearSearch}
        />
      ) : (
        <Table<TRow>
          columns={columns}
          dataSource={dataSource}
          rowHoverable
          rowSelection={rowSelection}
          rowClassName={rowClassName}
          aria-label={ariaLabel}
          sticky
          scroll={{ x: tableWidth, y: tableHeight }}
        />
      )}
    </Division>
  );
}
